import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-flexbox-grid';
import config from '../../config';
import TableRankingTeamItem from '../TableRankingTeamItem';
import EmptyState from '../EmptyState';
import { handleResponse, serializeQuery } from '../../utils/handleResponse';

const TableRankingTeam = ({
  cre,
  raceDay,
  raceEvent,
  setShowLoading,
  setShowSecondLoading,
  teamFavorites,
  saveTeamFavorite,
  deleteTeamFavorite,
  changeTeam,
  selectedGenderCategoryFilter,
  selectedCategoryFilter,
  searchText,
  overlapBadge,
  favoritesEnabled,
}) => {
  const [rakings, setRakings] = useState([]);
  const [rakingsFavorite, setRakingsFavorite] = useState([]);
  const isPreviewMode =
    new URLSearchParams(window.location.search).get('preview') === '1';
  const getElapsedValue = (raking) => (cre ? raking?.day_elapsed_time : raking?.elapsed_time);
  const hasMeaningfulTimes = rakings.some(
    (raking) => Number(getElapsedValue(raking) || 0) > 0
  );
  const shouldShowRankings = hasMeaningfulTimes || isPreviewMode;

  const normalizeAgeCategory = (rawValue) => {
    const value = String(rawValue || '')
      .trim()
      .toUpperCase();
    if (!value) return null;
    if (
      value.includes('AGE_100_PLUS') ||
      value.includes('AGE_100+') ||
      value.includes('100+')
    ) {
      return '100+';
    }
    if (value.includes('AGE_99') || value === '99' || value.includes('<=100')) {
      return '99';
    }
    if (value === '100') return '100+';
    return null;
  };

  const getTeamAgeCategory = (team) => {
    const participants = team?.participants || [];
    const participantCategory = participants
      .map((p) => normalizeAgeCategory(p?.category))
      .find(Boolean);

    if (participantCategory) return participantCategory;

    const teamCategory = normalizeAgeCategory(team?.teamAgeCategory);
    if (teamCategory) return teamCategory;

    return null;
  };

  const applyTeamAgeFilter = (teams) => {
    if (!selectedCategoryFilter) return teams;
    return teams.filter((team) => {
      const gender = String(team?.gender || '').toUpperCase();
      // Para mujeres no aplica filtro de edad por categoría.
      if (gender === 'WOMEN') return true;
      const ageCategory = getTeamAgeCategory(team);
      // Estricto: si no hay categoría persistida, no pasa el filtro.
      return ageCategory === selectedCategoryFilter;
    });
  };

  const normalizeSearch = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const applySearchFilter = (teams) => {
    const term = normalizeSearch(searchText).trim();
    if (!term) return teams;
    return teams.filter((team) => {
      const teamName = normalizeSearch(team?.name);
      if (teamName.includes(term)) return true;
      const participants = team?.participants || [];
      return participants.some((p) => {
        const fullName = normalizeSearch(`${p?.name || ''} ${p?.last_name || ''}`);
        return fullName.includes(term);
      });
    });
  };

  const getTeamRanking = async () => {
    setShowLoading(true);
    let athlete = null;
    try {
      athlete = JSON.parse(localStorage.getItem('userData') || 'null');
    } catch (e) {
      athlete = null;
    }
    const headers = athlete?.userToken
      ? {
          Authorization: `Bearer ${athlete.userToken}`,
        }
      : undefined;

    const requestOptions = {
      method: 'GET',
      ...(headers ? { headers } : {}),
    };

    const params = {
      page: 1,
      limit: 2000,
      orderByTotalDistance: true,
    };

    if (selectedGenderCategoryFilter) {
      params['gender'] = selectedGenderCategoryFilter;
    }
    if (cre) {
      params['cre'] = true;
    }

    // if (filterText) {
    //   params['search'] = filterText;
    // }

    const url = new URL(
      `${config.tncApiUrl}/sport-events/teams/${config.eventId}`
    );
    url.search = serializeQuery(params);

    const response = await fetch(url, requestOptions);

    setShowLoading(false);
    try {
      const newRankings = await handleResponse(response);
      console.log('newRankings', newRankings);
      const teams = newRankings.data.teams || [];
      setRakings(applyTeamAgeFilter(teams));
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (!rakings.length || !teamFavorites.length) {
      setRakingsFavorite([]);
      return;
    }
    // IDs (userId) que el usuario marcó como favoritos
    const favIdSet = new Set(teamFavorites.map((f) => f.teamId));

    const favTimes = rakings.filter((st) => favIdSet.has(st.id));
    setRakingsFavorite(favTimes);
  }, [rakings, teamFavorites]);

  useEffect(() => {
    if (raceDay) {
      getTeamRanking();
    }
  }, [raceDay]);

  useEffect(() => {
    getTeamRanking();
  }, [cre]);

  useEffect(() => {
    if (changeTeam) {
      getTeamRanking();
    }
  }, [changeTeam]);

  const visibleRakings = applySearchFilter(rakings);
  const visibleRakingsFavorite = applySearchFilter(rakingsFavorite);

  return (
    <Col xs={12}>
      <Row>
        {raceDay &&
          shouldShowRankings &&
          visibleRakings.length > 0 &&
          visibleRakingsFavorite.map((startingTime) => {
            return (
              <TableRankingTeamItem
                userInfo={startingTime}
                key={startingTime._id}
                firstRaking={visibleRakings[0]}
                raceDay={raceDay}
                teamFavorites={teamFavorites}
                saveTeamFavorite={saveTeamFavorite}
                deleteTeamFavorite={deleteTeamFavorite}
                darkMode
                overlapBadge={overlapBadge}
                favoritesEnabled={favoritesEnabled}
              />
            );
          })}
        {(!raceDay || !shouldShowRankings || visibleRakings.length === 0) && (
          <EmptyState />
        )}
        {raceDay &&
          shouldShowRankings &&
          visibleRakings.map((raking) => {
            if (cre && !raking.day_elapsed_time) return null;
            return (
              <TableRankingTeamItem
                cre={cre}
                userInfo={raking}
                key={raking._id}
                raceDay={raceDay}
                firstRaking={visibleRakings[0]}
                teamFavorites={teamFavorites}
                saveTeamFavorite={saveTeamFavorite}
                deleteTeamFavorite={deleteTeamFavorite}
                overlapBadge={overlapBadge}
                favoritesEnabled={favoritesEnabled}
              />
            );
          })}
      </Row>
    </Col>
  );
};

TableRankingTeam.propTypes = {
  setShowLoading: PropTypes.func.isRequired,
  saveTeamFavorite: PropTypes.func.isRequired,
  deleteTeamFavorite: PropTypes.func.isRequired,
  setShowSecondLoading: PropTypes.func.isRequired,
  raceDay: PropTypes.object.isRequired,
  raceEvent: PropTypes.object.isRequired,
  teamFavorites: PropTypes.array.isRequired,
  selectedGenderCategoryFilter: null,
  selectedCategoryFilter: PropTypes.string,
  searchText: PropTypes.string,
  overlapBadge: PropTypes.bool,
  changeTeam: PropTypes.number,
  cre: PropTypes.bool,
  sprint: PropTypes.bool,
  favoritesEnabled: PropTypes.bool,
};

TableRankingTeam.defaultProps = {
  selectedGenderCategoryFilter: null,
  selectedCategoryFilter: null,
  searchText: '',
  overlapBadge: false,
  changeTeam: null,
  cre: false,
  favoritesEnabled: false,
};

export default TableRankingTeam;
